"use client";

import * as React from "react";
import {
  format,
  parseISO,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subDays,
} from "date-fns";
import { ru } from "date-fns/locale";
import { IconCalendar, IconClose } from "@/components/ui/icons";
import type { DateRange } from "react-day-picker";
import { cn } from "@/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseIso(value: string): Date | undefined {
  if (!value) return undefined;
  const d = parseISO(value);
  return isValid(d) ? d : undefined;
}

function toIso(d: Date | undefined): string {
  if (!d) return "";
  return format(d, "yyyy-MM-dd");
}

function formatDisplay(d: Date | undefined, fmt = "d MMMM yyyy"): string {
  if (!d) return "";
  return format(d, fmt, { locale: ru });
}

// ─── Single DatePicker ────────────────────────────────────────────────────────

type DatePickerProps = {
  value: string; // "YYYY-MM-DD" or ""
  onChange: (val: string) => void;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  /** "input" — looks like a bordered input field
   *  "inline" — minimal text button */
  variant?: "input" | "inline";
  size?: "sm" | "md";
  clearable?: boolean;
  align?: "left" | "right";
  className?: string;
  /** Формат отображения выбранной даты (date-fns). По умолчанию "d MMMM yyyy". */
  displayFormat?: string;
  /** Доп. класс для выпадающего контента (напр. z-index поверх модалок). */
  contentClassName?: string;
  /** Разрешённые даты («YYYY-MM-DD»). Остальные дни в календаре недоступны —
   *  нужно там, где данные есть не за каждый день (снимки остатков по складам). */
  allowedDates?: string[];
  /** Скрыть «Очистить» — когда пустое значение бессмысленно (нужен конкретный день). */
  hideClear?: boolean;
};

export function DatePicker({
  value,
  onChange,
  placeholder = "Выбрать дату",
  disabled = false,
  variant = "input",
  size = "sm",
  clearable = true,
  align = "left",
  className,
  displayFormat,
  contentClassName,
  allowedDates,
  hideClear,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseIso(value);
  const allowed = React.useMemo(
    () => (allowedDates && allowedDates.length ? new Set(allowedDates) : null),
    [allowedDates],
  );

  const triggerClass =
    variant === "input"
      ? size === "sm"
        ? "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[11px] text-[var(--text)] hover:border-[var(--primary)]/50 transition min-w-[120px] focus:outline-none disabled:opacity-50"
        : "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[12px] text-[var(--text)] hover:border-[var(--primary)]/50 transition min-w-[140px] focus:outline-none disabled:opacity-50"
      : "flex items-center gap-1.5 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[12px] transition glass-hover disabled:opacity-50";

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={cn("glass-flat", triggerClass, className)}>
          {variant === "input" && (
            <IconCalendar size={size === "sm" ? 12 : 14} className="shrink-0 text-[var(--textMuted)]" />
          )}
          {selected ? (
            <span className="flex-1 text-left">{formatDisplay(selected, displayFormat)}</span>
          ) : (
            <span className="flex-1 text-left text-[var(--textMuted)]">{placeholder}</span>
          )}
          {clearable && value && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onChange("");
                }
              }}
              className="shrink-0 text-[var(--textMuted)] hover:text-[var(--text)] transition"
            >
              <IconClose size={10} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align === "right" ? "end" : "start"} sideOffset={4} className={cn("w-auto p-0", contentClassName)}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            onChange(toIso(d));
            setOpen(false);
          }}
          defaultMonth={selected ?? (allowedDates?.length ? parseIso(allowedDates[0]) : undefined)}
          disabled={allowed ? (d: Date) => !allowed.has(toIso(d)) : undefined}
        />
        {!hideClear && (
          <div className="border-t border-[var(--border)] px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="text-[11px] text-[var(--textMuted)] hover:text-[var(--text)] transition"
            >
              Очистить
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────

export type IsoDateRange = { from: string; to: string };

type DateRangePickerProps = {
  value: IsoDateRange;
  onChange: (val: IsoDateRange) => void;
  /** ReactNode, а не строка: в плотных строках вместо слова ставят значок
   *  календаря — место под срок видно, а подписи в каждой строке не шумят. */
  placeholder?: React.ReactNode;
  disabled?: boolean;
  variant?: "input" | "inline";
  size?: "sm" | "md";
  clearable?: boolean;
  align?: "left" | "right";
  className?: string;
  numberOfMonths?: number;
  /** Формат показа дат (date-fns). По умолчанию «d MMMM yyyy», как у DatePicker.
   *  В плотных строках («Подзадачи») ставят короткий — «d MMM». */
  displayFormat?: string;
};

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Выбрать период",
  disabled = false,
  variant = "input",
  size = "sm",
  clearable = true,
  align = "left",
  className,
  numberOfMonths = 2,
  displayFormat,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const from = parseIso(value.from);
  const to = parseIso(value.to);
  const range: DateRange | undefined = from || to ? { from, to } : undefined;

  const displayText = (() => {
    const ф = (d: Date) => formatDisplay(d, displayFormat);
    if (from && to) return `${ф(from)} — ${ф(to)}`;
    if (from) return `с ${ф(from)}`;
    if (to) return `по ${ф(to)}`;
    return "";
  })();

  const triggerClass =
    variant === "input"
      ? size === "sm"
        ? "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[11px] text-[var(--text)] hover:border-[var(--primary)]/50 transition min-w-[200px] focus:outline-none disabled:opacity-50"
        : "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[12px] text-[var(--text)] hover:border-[var(--primary)]/50 transition min-w-[240px] focus:outline-none disabled:opacity-50"
      : "flex items-center gap-1.5 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[12px] transition glass-hover disabled:opacity-50";

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange({ from: "", to: "" });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={cn("glass-flat", triggerClass, className)}>
          {variant === "input" && (
            <IconCalendar size={size === "sm" ? 12 : 14} className="shrink-0 text-[var(--textMuted)]" />
          )}
          {displayText ? (
            <span className="flex-1 text-left truncate">{displayText}</span>
          ) : (
            <span className="flex-1 text-left text-[var(--textMuted)]">{placeholder}</span>
          )}
          {clearable && (from || to) && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onChange({ from: "", to: "" });
                }
              }}
              className="shrink-0 text-[var(--textMuted)] hover:text-[var(--text)] transition"
            >
              <IconClose size={10} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align === "right" ? "end" : "start"} sideOffset={4} className="w-auto p-0">
        <Calendar
          mode="range"
          selected={range}
          onSelect={(r) => {
            onChange({ from: toIso(r?.from), to: toIso(r?.to) });
          }}
          numberOfMonths={numberOfMonths}
          defaultMonth={from ?? new Date()}
        />
        <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-2.5">
          <button
            type="button"
            onClick={() => onChange({ from: "", to: "" })}
            className="text-[11px] text-[var(--textMuted)] hover:text-[var(--text)] transition"
          >
            Очистить
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-[var(--radius-md)] bg-[var(--primary)] px-2.5 py-1 text-[11px] font-semibold text-[var(--primaryText)] hover:opacity-90 transition"
          >
            Готово
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── PeriodRangePicker (mppulse-style, draft + Apply) ──────────────────────────
// Единый range-календарь периода: один триггер-кнопка с подписью диапазона →
// поповер с ДВУМЯ месяцами, пресетами, «Сбросить» и «Применить». Выбор копится
// в локальном черновике; в onApply (filterFrom/filterTo) пишется ТОЛЬКО по
// «Применить», чтобы вкладки не пересчитывались на каждый клик.

export type PeriodRangePickerProps = {
  /** "YYYY-MM-DD" or "" */
  from: string;
  to: string;
  /** Коммит выбора (по «Применить» / пресету «Сбросить»). */
  onApply: (from: string, to: string) => void;
  align?: "left" | "right";
  className?: string;
};

/** Дефолт «Сбросить» — последние 7 дней включая сегодня. */
function defaultRange(): { from: Date; to: Date } {
  const to = new Date();
  return { from: subDays(to, 6), to };
}

export function PeriodRangePicker({ from, to, onApply, align = "left", className }: PeriodRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  // Локальный черновик выбора. Синхронизируется с пропсами при открытии.
  const [draft, setDraft] = React.useState<DateRange | undefined>(undefined);

  const fromD = parseIso(from);
  const toD = parseIso(to);

  // При открытии — подтянуть актуальный период из пропсов в черновик.
  React.useEffect(() => {
    if (open) setDraft(fromD || toD ? { from: fromD, to: toD } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const triggerText = (() => {
    if (fromD && toD) return `${formatDisplay(fromD)} – ${formatDisplay(toD)}`;
    if (fromD) return `с ${formatDisplay(fromD)}`;
    if (toD) return `по ${formatDisplay(toD)}`;
    return "Период";
  })();

  const draftText = (() => {
    if (draft?.from && draft?.to) return `${formatDisplay(draft.from)} – ${formatDisplay(draft.to)}`;
    if (draft?.from) return `${formatDisplay(draft.from)} – …`;
    return "Выберите диапазон";
  })();

  function applyPreset(kind: "week" | "month" | "quarter") {
    const today = new Date();
    let f: Date;
    let t: Date;
    if (kind === "week") {
      f = subDays(today, 6);
      t = today;
    } else if (kind === "month") {
      f = startOfMonth(today);
      t = endOfMonth(today);
    } else {
      f = startOfQuarter(today);
      t = endOfQuarter(today);
    }
    setDraft({ from: f, to: t });
  }

  function reset() {
    const { from: f, to: t } = defaultRange();
    onApply(toIso(f), toIso(t));
    setOpen(false);
  }

  function apply() {
    const f = draft?.from;
    const t = draft?.to ?? draft?.from; // если выбран только один день — диапазон в один день
    if (f) onApply(toIso(f), toIso(t));
    setOpen(false);
  }

  const presetBtn =
    "glass-nav rounded-[var(--radius-md)] px-2.5 py-1 text-[11px] font-semibold text-[var(--textMuted)] hover:text-[var(--text)] transition";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "glass-flat flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[11px] text-[var(--text)] hover:border-[var(--primary)]/50 transition focus:outline-none",
            className,
          )}
        >
          <IconCalendar size={12} className="shrink-0 text-[var(--textMuted)]" />
          <span className="text-left whitespace-nowrap">{triggerText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align={align === "right" ? "end" : "start"} sideOffset={4} className="w-auto p-0">
        {/* Пресеты */}
        <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-2.5">
          <button type="button" className={presetBtn} onClick={() => applyPreset("week")}>Неделя</button>
          <button type="button" className={presetBtn} onClick={() => applyPreset("month")}>Месяц</button>
          <button type="button" className={presetBtn} onClick={() => applyPreset("quarter")}>Квартал</button>
        </div>
        <Calendar
          mode="range"
          selected={draft}
          onSelect={(r) => setDraft(r)}
          numberOfMonths={2}
          defaultMonth={draft?.from ?? fromD ?? new Date()}
        />
        {/* Подпись выбранного диапазона */}
        <div className="border-t border-[var(--border)] px-4 pt-2.5 text-[11px] text-[var(--textMuted)]">
          {draftText}
        </div>
        {/* Действия */}
        <div className="flex items-center justify-between px-4 py-2.5">
          <button
            type="button"
            onClick={reset}
            className="text-[11px] text-[var(--textMuted)] hover:text-[var(--text)] transition"
          >
            Сбросить
          </button>
          <button
            type="button"
            onClick={apply}
            className="rounded-[var(--radius-md)] bg-[var(--primary)] px-3 py-1 text-[11px] font-semibold text-[var(--primaryText)] hover:opacity-90 transition"
          >
            Применить
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
